import type { ImageBlockDto } from "@annotate-dev/dtos";
import { WalkthroughView, getWalkthroughData } from "@annotate-dev/react";
import { json, redirect, type MetaArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import invariant from "tiny-invariant";
import { prepareHtmlMetaDataDescriptor } from "~/lib/utils.js";
import { loaderWrapper } from "~/lib/utils.server.js";

export function meta({ data, location }: MetaArgs<typeof loader>) {
  if (!data) {
    return { title: "Annotate" };
  }

  const { walkthrough, publishedBlocks } = data.walkthroughData;

  if (!walkthrough.publishedVersion) {
    return [{ title: "Annotate" }];
  }

  const htmlDescription = walkthrough.publishedVersion.description;
  const descriptionWithHtmlTagsRemoved = htmlDescription.replace(
    /<[^>]*>/g,
    "",
  );

  const imageBlocks =
    publishedBlocks.filter((b): b is ImageBlockDto => b.type === "image") ?? [];

  const prefetchLinks = imageBlocks.map((i) => ({
    rel: "prefetch",
    href: i.url,
    tagName: "link",
  }));

  return [
    ...prepareHtmlMetaDataDescriptor({
      title: walkthrough.publishedVersion.title,
      description: descriptionWithHtmlTagsRemoved,
      baseUrl: data.origin,
      urlPath: location.pathname,
    }),
    ...prefetchLinks,
  ];
}

export const loader = loaderWrapper(async ({ request, params, api }) => {
  const { slug } = params as { slug: string };

  // The slug is in the format of `{walkthroughTitle}-{walkthroughId}`
  const walkthroughId = slug.split("-").pop();

  if (!walkthroughId) {
    throw new Error("Invalid slug");
  }

  const walkthroughData = await getWalkthroughData(walkthroughId);
  const { walkthrough } = walkthroughData;

  // Non published walkthroughs result in a HTTP error response
  invariant(walkthrough.publishedVersion);

  const url = new URL(request.url);
  const expectedPathname = `/p/${walkthrough.workspaceName}/${walkthrough.publishedVersion.slug}`;
  if (url.pathname !== expectedPathname) {
    return redirect(expectedPathname);
  }

  return json({
    walkthroughData,
    origin: process.env.ORIGIN!,
  });
});

export function headers() {
  if (process.env.NODE_ENV === "development") {
    return {};
  }

  const days30 = 60 * 60 * 24 * 30;
  return {
    "Cache-Control": `public, max-age=1, s-maxage=60, stale-while-revalidate=${days30}`,
  };
}

export default function Route() {
  const { walkthroughData } = useLoaderData<typeof loader>();

  return (
    <div className="h-[100dvh] w-full">
      <WalkthroughView data={walkthroughData} />
    </div>
  );
}
